import axios from "axios";
import Favico from "favico.js";
import Pusher from "pusher-js";
import { ref, computed, watch, watchEffect, nextTick, onUnmounted } from "vue";
import { usePage } from "@inertiajs/vue3";
import { getSpecificUserData, userStore } from "@/Helpers/store.js";

export const unreadLinaMessagesCount = ref(0);
export const unreadNotificationsCount = ref(0);
export const readNotifications = ref([]);
export const unreadVisitsCount = ref(0);
export const unreadConversations = ref([]);
export const unreadHiddenConversations = ref([]);
export const readConversations = ref([]);
export const displayLinaNotification = ref(true);
export const hasUnreadLinaMessages = computed(
    () => unreadLinaMessagesCount.value && displayLinaNotification.value
);

export const markLeanplumMessagesAsRead = () => {
    displayLinaNotification.value = false;
};

export const leanplumNotification = {
    data: {
        avatar: "https://res.cloudinary.com/daotmu8d3/image/upload/c_fit,f_auto,q_auto,w_auto/dpr_auto/v1649061178/Assets/Notifications/Lina.svg",
        icon: "https://res.cloudinary.com/daotmu8d3/image/upload/c_fit,f_auto,q_auto,w_auto/dpr_auto/v1647608653/Assets/Notifications/bouncer.svg",
        message: "notifications.leanplum.sent_message",
        title: "notifications.leanplum.title",
    },
    type: "leanplumMessageNotification",
};

export const notificationCount = computed(() => {
    return Math.max(
        0,
        unreadNotificationsCount.value +
            (hasUnreadLinaMessages.value ? 1 : 0) -
            readNotifications.value.length
    );
});

export const unreadUnhiddenConversations = computed(() => {
    return unreadConversations.value.filter(
        (conversationId) =>
            !unreadHiddenConversations.value.includes(conversationId)
    );
});

export const messengerCount = computed(() => {
    const conversationMap = new Map();

    unreadConversations.value.forEach((conversationId) => {
        conversationMap.set(conversationId, conversationId);
    });

    let listOfunreadConversations = Array.from(conversationMap.values());

    listOfunreadConversations = listOfunreadConversations.filter(
        (conversation) => !readConversations.value.includes(conversation)
    );

    const unreadCount = listOfunreadConversations.length;

    return hasUnreadLinaMessages.value ? unreadCount + 1 : unreadCount;
});

export const unhiddenMessengerCount = computed(() => {
    const conversationMap = new Map();

    unreadUnhiddenConversations.value.forEach((conversationId) => {
        conversationMap.set(conversationId, conversationId);
    });

    let listOfunreadConversations = Array.from(conversationMap.values());

    listOfunreadConversations = listOfunreadConversations.filter(
        (conversationId) => !readConversations.value.includes(conversationId)
    );

    return listOfunreadConversations.length;
});

export const hiddenMessengerCount = computed(() => {
    const conversationMap = new Map();

    unreadHiddenConversations.value.forEach((conversationId) => {
        conversationMap.set(conversationId, conversationId);
    });

    let listOfunreadConversations = Array.from(conversationMap.values());

    listOfunreadConversations = listOfunreadConversations.filter(
        (conversationId) => !readConversations.value.includes(conversationId)
    );

    return listOfunreadConversations.length;
});

export const initCounts = async () => {
    try {
        const response = await axios.get(route("data.get.counts"));

        unreadConversations.value = response.data.unread_conversations;
        unreadHiddenConversations.value =
            response.data.unread_hidden_conversations;
        unreadNotificationsCount.value = response.data.unread_notifications;
        unreadLinaMessagesCount.value = response.data.unread_lina_messages;
        unreadVisitsCount.value = response.data.unread_visits;
    } catch (error) {
        console.error("Error fetching unread counts:", error);
    }
};

export const notificationData = ref([]);
export const canLoadMoreNotifications = ref(false);

const notificationMeta = ref(null);
let fetchingNotifications = false;

export const fetchNotifications = async () => {
    if (fetchingNotifications) return;
    fetchingNotifications = true;
    try {
        const response = await axios.get(
            notificationMeta.value ?? route("data.fetch.notifications")
        );
        if (!response.data) return;
        notificationData.value.push(...response.data.notifications.data);
        notificationMeta.value = response.data.notifications.next_page_url;
        canLoadMoreNotifications.value = !!notificationMeta.value;
    } finally {
        fetchingNotifications = false;
    }
};

export const deleteNotifications = () => {
    notificationData.value = [];
    notificationMeta.value = null;
    canLoadMoreNotifications.value = false;
    readNotifications.value = [];
    fetchingNotifications = false;
};

export const markNotificationAsRead = (id) => {
    const notification = notificationData.value.find(
        (n) => String(n.id) === String(id)
    );
    if (notification && !readNotifications.value.includes(notification)) {
        readNotifications.value.push(notification);
    }
};

const MESSENGER_NOTIFICATION_TYPES = Object.freeze([
    "SentSmiley",
    "SharedData",
    "SharedPhoto",
    "AskedQuestionGameQuestion",
    "AnsweredQuestionGameQuestion",
    "SaidHi",
    "SaidHello",
    "LikedContent",
    "SentMessage",
    "AnsweredPersonalQuestion",
    "ProfileInformationEnquired",
    "SentRose",
    "MadeCompliment",
    "SentExclusiveMessage",
]);

export const subscribeToPusher = (userId, pusherKey) => {
    const pusher = new Pusher(pusherKey, { cluster: "eu", encrypted: true });
    const notificationChannelName = `notification.channel.${userId}`;
    const userDataChannelName = `user.data.channel.${userId}`;

    const notificationChannel = pusher.subscribe(notificationChannelName);

    notificationChannel.bind("notification.created", (data) => {
        notificationData.value.unshift(data.notification);
        unreadNotificationsCount.value += 1;

        if (data.notification.type === "FirstProfileVisit") {
            unreadVisitsCount.value += 1;
            return;
        }
        if (data.notification.type === "CoinsReceivedForReward") {
            getSpecificUserData("rewards");
            return;
        }
        if (data.notification.type === "PromotionRecieved") {
            getSpecificUserData("promotion");
            return;
        }
        if (!MESSENGER_NOTIFICATION_TYPES.includes(data.notification.type))
            return;

        if (!unreadConversations.value.includes(data.notification.partner_id)) {
            unreadConversations.value.push(data.notification.partner_id);
        }
        const index = readConversations.value.indexOf(
            data.notification.partner_id
        );
        if (index > -1) {
            readConversations.value.splice(index, 1);
        }
    });

    notificationChannel.bind("notifications.deleted.one.way", (data) => {
        notificationData.value = notificationData.value.filter(
            (n) => n.partner_id !== data.partner_id
        );
        initCounts();
    });

    notificationChannel.bind("notifications.deleted.two.way", (data) => {
        notificationData.value = notificationData.value.filter(
            (n) =>
                n.partner_id !== data.user_id &&
                n.partner_id !== data.partner_id
        );
        initCounts();
    });

    const userDataChannel = pusher.subscribe(userDataChannelName);
    userDataChannel.bind("user.data.changed", (data) => {
        userStore.data = data.data;
    });

    return () => {
        pusher.unsubscribe(notificationChannelName);
        pusher.unsubscribe(userDataChannelName);
    };
};

export const useNotificationPusher = (user) => {
    const pusherKey = usePage().props.mix.pusher_key;
    let unsubscribe = null;

    const stopWatching = watchEffect(() => {
        if (user.value?.id) {
            unsubscribe = subscribeToPusher(user.value.id, pusherKey);
            nextTick(() => stopWatching());
        }
    });

    onUnmounted(() => {
        unsubscribe?.();
    });
};

const favicon = new Favico({
    animation: "slide",
});

watch(notificationCount, (newCount) => {
    favicon.badge(newCount);
});
